import { useEffect, useRef } from "react";
import { MicrophoneIcon } from "@heroicons/react/24/outline";

/**
 * MicLevelMeter — live input-level bar for the microphone dropdown footer.
 *
 * Feeds the mic audio through a Web Audio AnalyserNode and paints the running
 * RMS as an orange fill bar. The bar width is written straight to the DOM in
 * the animation-frame loop (via a ref) so we don't re-render every frame.
 *
 * Pass either a MediaStream or a single MediaStreamTrack. With VideoSDK's
 * useParticipant micStream (a wrapper carrying `.track`), pass:
 *   <MicLevelMeter track={micStream?.track} />
 *
 * @param {MediaStream} [stream]
 * @param {MediaStreamTrack} [track]
 */
export default function MicLevelMeter({ stream, track }) {
  const fillRef = useRef(null);

  useEffect(() => {
    // Normalize whatever we were given into a MediaStream with a live track.
    let mediaStream = null;
    if (stream instanceof MediaStream) {
      mediaStream = stream;
    } else if (track) {
      mediaStream = new MediaStream([track]);
    }
    if (!mediaStream || mediaStream.getAudioTracks().length === 0) return;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    let ctx;
    let rafId;
    let closed = false;

    try {
      ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(mediaStream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser); // NOT connected to destination — no echo.

      const data = new Uint8Array(analyser.frequencyBinCount);
      // Some browsers start the context suspended until a user gesture.
      if (ctx.state === "suspended") ctx.resume().catch(() => {});

      let smoothed = 0;
      const tick = () => {
        if (closed) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128; // -1..1
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const level = Math.min(1, rms * 2.5); // gentle boost so speech fills it
        // Ease down quickly, follow up instantly — feels responsive but calm.
        smoothed = level > smoothed ? level : smoothed * 0.85 + level * 0.15;
        if (fillRef.current) {
          fillRef.current.style.width = `${Math.round(smoothed * 100)}%`;
        }
        rafId = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      // Web Audio unavailable/blocked — leave the bar empty rather than crash.
      return;
    }

    return () => {
      closed = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (ctx && ctx.state !== "closed") ctx.close().catch(() => {});
      if (fillRef.current) fillRef.current.style.width = "0%";
    };
  }, [stream, track]);

  return (
    <div className="flex items-center gap-2 px-2 py-1.5">
      <MicrophoneIcon
        className="h-5 w-5 shrink-0 text-gray-150"
        aria-hidden="true"
      />
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
        <div
          ref={fillRef}
          className="h-full w-0 rounded-full bg-orange-150 transition-[width] duration-75 ease-out motion-reduce:transition-none"
        />
      </div>
    </div>
  );
}
